/*
 * File: /apps/web/src/build/redux/modules/taskerReviews copy.js
 * Project: tr-web
 * Created Date: 2020-07-16
 * Author: Raif
 *
 * Copyright (c) 2020 TaskRabbit, Inc
 */
import { NetworkService } from "services/NetworkService";
import {
  createActionTypesFor,
  deepMergeTaskerCategoryItems,
  updatePagingOnCategory,
} from "util/reducerHelpers";
import { lazyPager } from "util/lazyPager";

/* schema
  taskers: [
    {
      taskerId: number,
      categories: [
        {
          categoryId: number,
          paging: {
            totalItems: number,
            itemsPerPage: number,
            currentPage: number,
          }
          reviews: [],
        },
      ],
    },
  ],
  loading: bool
} */
const initialState = {
  taskers: [],
  loading: false,
};

const TASKER_REVIEWS = createActionTypesFor(
  "v3/tasker/taskerReviews",
  "taskerReviews"
);
const TASKER_REVIEWS_UPDATE_PAGING =
  "v3/tasker/taskerReviews/TASKER_REVIEWS_UPDATE_PAGING";

const calcAvgRating = (items) => {
  if (items.length === 0) {
    return 0;
  }
  const ratingSum = items.reduce((acc, item) => acc + item.display_rating, 0);
  const ratingAvg =  ratingSum / items.length;
  return Number(ratingAvg.toFixed(1));
};

const calcDistributionByRating = (items) => {
  const distributionByRating = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  items.forEach((item) => {
    if (item.display_rating in distributionByRating) {
      distributionByRating[item.display_rating] += 1;
    }
  });
  return distributionByRating;
};

const limitResult = (result, limit) => {
  if (result && limit && result.totalItems > limit) {
    result.totalItems = limit;
    result.items.length = limit;
    result.totalReviewCount = limit;
    result.totalPages = 1;
    result.distributionByRating = calcDistributionByRating(result.items);

    if (result.reviewStarRatingDisplay) {
      result.reviewStarRatingDisplay.description = '';
      result.reviewStarRatingDisplay.stat = calcAvgRating(result.items);
    }
  }
  return result;
};

export default function taskerReviews(state = initialState, action) {
  switch (action.type) {
    case TASKER_REVIEWS.REQUEST:
      return {
        ...state,
        loading: true,
      };

    case TASKER_REVIEWS_UPDATE_PAGING: {
      return {
        ...state,
        loading: false,
        taskers: updatePagingOnCategory({
          action,
          state,
        }),
      };
    }
    case TASKER_REVIEWS.SUCCESS: {
      const taskers = deepMergeTaskerCategoryItems({
        action,
        state,
        collectionPropertyName: "reviews",
        itemIdKey: "reviewId",
      });
      // this puts some data on the tasker it's self that is
      // a rollup of their categories data.
      const taskersWithAggregateReviews = taskers.map((t) => {
        if (t.taskerId === action.taskerId) {
          return {
            ...t,
            overAllStat: action.overAllStat,
            overAllDisplay: action.overAllDisplay,
          };
        }
        return t;
      });
      const newState = { ...state, taskers: taskersWithAggregateReviews };
      return {
        ...newState,
        loading: false,
      };
    }

    case TASKER_REVIEWS.FAILURE:
      return {
        ...state,
        loading: false,
      };
    default:
      return state;
  }
}

export const fetchReviewsByCategoryWithPaging = (
  taskerId,
  categoryId,
  paging = { currentPage: 1, itemsPerPage: 5 },
  newPage = 1,
  limit,
) => async (dispatch) => {
  const { fetchQuantity, fetchFromIndex, shouldFetch } = lazyPager(
    paging,
    newPage
  );

  if (!shouldFetch) {
    return dispatch({
      type: TASKER_REVIEWS_UPDATE_PAGING,
      taskerId,
      categoryId,
      paging: {
        currentPage: newPage,
      },
    });
  }

  dispatch({
    type: TASKER_REVIEWS.REQUEST,
    loading: true,
  });

  try {
    const result = (
      await NetworkService.fetchTaskerReviews(
        taskerId,
        categoryId,
        fetchFromIndex,
        limit || fetchQuantity,
        limit
      )
    ).camelCasedData;

    const { totalItems, items: reviews } = limitResult(result);
    const overAllDisplay = result?.reviewStarRatingDisplay?.description;
    const overAllStat = result?.reviewStarRatingDisplay?.stat;

    // eslint-disable-next-line consistent-return
    return dispatch({
      type: TASKER_REVIEWS.SUCCESS,
      taskerId,
      categoryId,
      paging: {
        currentPage: newPage,
        totalItems,
        itemsPerPage: paging.itemsPerPage,
      },
      overAllDisplay,
      overAllStat,
      reviews,
    });
  } catch (error) {
    dispatch({ type: TASKER_REVIEWS.FAILURE, error });
  }
  return null;
};
